import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import Spinner from "../../../components/common/spinner/Spinner";
import { showToast } from "../../../components/common/notification/Notification";
import DateLabel from "../../../components/common/DateLabel";

const getErrorText = (err, fallback) => err?.response?.data?.text || err?.message || fallback;

const LeaseNotesModal = ({ show, onHide, lease, leaseService, currentUserId, onChanged }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchNotes = useCallback(async () => {
    if (!lease?.id) return;
    try {
      setLoading(true);
      const data = await leaseService.listNotes(lease.id);
      setNotes(data?.leaseNotes || []);
    } catch (err) {
      showToast(getErrorText(err, "Failed to load notes"), { type: "error" });
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [lease, leaseService]);

  useEffect(() => {
    if (show) {
      setText("");
      setEditingId(null);
      fetchNotes();
    }
  }, [show, fetchNotes]);

  if (!show || !lease) return null;

  const handleSave = async (e) => {
    e.preventDefault();
    const value = text.trim();
    if (!value) {
      showToast("Please enter a note", { type: "warning" });
      return;
    }
    try {
      setSaving(true);
      if (editingId) {
        await leaseService.editNote(editingId, value);
        showToast("Note updated", { type: "success" });
      } else {
        await leaseService.addNote(lease.id, value);
        showToast("Note added", { type: "success" });
      }
      setText("");
      setEditingId(null);
      await fetchNotes();
      if (onChanged) onChanged();
    } catch (err) {
      showToast(getErrorText(err, "Failed to save note"), { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (note) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await leaseService.removeNote(note.id);
      showToast("Note deleted", { type: "success" });
      if (editingId === note.id) {
        setEditingId(null);
        setText("");
      }
      await fetchNotes();
      if (onChanged) onChanged();
    } catch (err) {
      showToast(getErrorText(err, "Failed to delete note"), { type: "error" });
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "18px", fontWeight: 700 }}>
          Notes{lease.title ? ` · ${lease.title}` : ""}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <Spinner loading={true} />
          </div>
        ) : notes.length === 0 ? (
          <p className="text-muted text-center my-3">No notes yet.</p>
        ) : (
          <div className="d-flex flex-column gap-2">
            {notes.map((note) => {
              const isMine = Number(note.user?.id) === Number(currentUserId);
              return (
                <div key={note.id} className="border rounded-3 p-2 px-3">
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div>
                      <div className="small fw-semibold">
                        {[note.user?.first_name, note.user?.last_name].filter(Boolean).join(" ") || "User"}
                      </div>
                      <div style={{ whiteSpace: "pre-wrap" }}>{note.note}</div>
                      <DateLabel as="div" className="text-muted small mt-1" value={note.created_on} withTime />
                    </div>
                    {isMine && (
                      <div className="d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          title="Edit note"
                          onClick={() => {
                            setEditingId(note.id);
                            setText(note.note);
                          }}
                        >
                          <FiEdit2 size={14} />
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          title="Delete note"
                          onClick={() => handleDelete(note)}
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="d-block">
        <form onSubmit={handleSave} className="d-flex flex-column gap-2">
          <textarea
            rows={2}
            className="form-control"
            placeholder={editingId ? "Edit your note" : "Add a note"}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <div className="d-flex justify-content-end gap-2">
            {editingId && (
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => {
                  setEditingId(null);
                  setText("");
                }}
              >
                Cancel edit
              </button>
            )}
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving..." : editingId ? "Update Note" : "Add Note"}
            </button>
          </div>
        </form>
      </Modal.Footer>
    </Modal>
  );
};

export default LeaseNotesModal;
